import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../molecules/PageHeader';
import TextEdit from '../../molecules/TextEdit';
import FileUpload from '../../molecules/FileUpload';
import ActionButton from '../../molecules/ActionButton';
import Loading from '../../ions/Loading';
import Dialog from '../../organisms/Dialog';
import { uploadInBatches, BatchUploadProgress } from '../../../services/BatchUploadService';
import { STRUCTURING_ROUTES, STRUCTURING_BREADCRUMBS } from './structuringUtils';
import {
    FormData,
    UploadState,
    validateForm,
    showValidationError,
    showSuccess,
    showError,
} from './OperationPageFormBackend';

const OperationPageForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<FormData>({ operationName: '', files: [] });
    const [uploadState, setUploadState] = useState<UploadState>({
        isProcessing: false,
        summary: null,
        processingStatus: '',
        processedFiles: [],
        showSummary: false,
        operationCode: '',
        uploadProgress: null,
    });

    const goToOperationsList = () => {
        navigate(STRUCTURING_ROUTES.list);
    };

    const handleSave = async () => {
        const validation = validateForm(formData);
        if (!validation.valid) {
            showValidationError(validation.error!);
            return;
        }

        try {
            setUploadState(prev => ({ ...prev, isProcessing: true, uploadProgress: null }));

            const result = await uploadInBatches(
                formData.operationName,
                formData.files,
                30,
                (progress: BatchUploadProgress) => {
                    setUploadState(prev => ({
                        ...prev,
                        uploadProgress: progress,
                        processingStatus: `Processando lote ${progress.currentBatch} de ${progress.totalBatches}...`
                    }));
                }
            );

            if (result.success) {
                setUploadState(prev => ({
                    ...prev,
                    summary: result.summary,
                    operationCode: result.operation_code,
                    showSummary: true
                }));
                showSuccess('Operação processada com sucesso!');
            } else {
                setUploadState(prev => ({
                    ...prev,
                    summary: result.summary,
                    operationCode: result.operation_code,
                    showSummary: true
                }));
                if (result.errors.length > 0) {
                    showError(`Upload parcialmente concluído. ${result.errors.length} lotes falharam.`);
                } else {
                    showError('Erro no processamento');
                }
            }
        } catch (err: any) {
            showError(err.message || 'Erro de conexão com o servidor');
        } finally {
            setUploadState(prev => ({ ...prev, isProcessing: false, uploadProgress: null }));
        }
    };

    const handleCloseDialog = () => {
        setUploadState({
            isProcessing: false,
            summary: null,
            processingStatus: '',
            processedFiles: [],
            showSummary: false,
            operationCode: '',
            uploadProgress: null,
        });
        setFormData({ operationName: '', files: [] });
        goToOperationsList();
    };

    return (
        <div className="p-4 relative">
            {uploadState.isProcessing && <Loading />}
            <PageHeader
                title="Nova operação"
                breadcrumb={[
                    STRUCTURING_BREADCRUMBS.structuring,
                    STRUCTURING_BREADCRUMBS.newOperation,
                ]}
                apiVersion=""
            >
                <ActionButton
                    label={uploadState.isProcessing ? 'Processando...' : 'Salvar'}
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleSave}
                />
            </PageHeader>
            <div className={`grid gap-4 mt-4 ${uploadState.isProcessing ? 'pointer-events-none opacity-50' : ''}`}>
                <TextEdit
                    label="Nome da operação"
                    value={formData.operationName}
                    onChange={(e) => setFormData(prev => ({ ...prev, operationName: e.target.value }))}
                    disabled={uploadState.isProcessing}
                />

                {uploadState.uploadProgress && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                {uploadState.processingStatus}
                            </span>
                            <span className="text-sm text-blue-600 dark:text-blue-400">
                                {uploadState.uploadProgress.processedFiles}/{uploadState.uploadProgress.totalFiles} arquivos
                            </span>
                        </div>
                        <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(uploadState.uploadProgress.processedFiles / uploadState.uploadProgress.totalFiles) * 100}%` }}
                            ></div>
                        </div>
                        <div className="flex justify-between text-xs text-blue-600 dark:text-blue-400 mt-1">
                            <span>Lote {uploadState.uploadProgress.currentBatch}/{uploadState.uploadProgress.totalBatches}</span>
                            <span>{Math.round((uploadState.uploadProgress.processedFiles / uploadState.uploadProgress.totalFiles) * 100)}%</span>
                        </div>
                    </div>
                )}
            </div>
            <div className="mt-4">
                <FileUpload
                    accept=".xml"
                    name="files"
                    label="Arquivos"
                    errorMessage=""
                    onUploadFile={(uploadedFile) => setFormData(prev => ({ ...prev, files: [...prev.files, uploadedFile] }))}
                    onRemoveFile={() => setFormData(prev => ({ ...prev, files: [] }))}
                    disabled={uploadState.isProcessing}
                    multiple={true}
                />
            </div>
            <Dialog
                id="summary-dialog"
                show={uploadState.showSummary}
                onClose={handleCloseDialog}
                header="Resumo do processamento"
                body={
                    <div className="p-4">
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="font-medium">Operação:</span>
                                <span>{uploadState.operationCode}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Total de arquivos:</span>
                                <span>{uploadState.summary?.total_files || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Uploads bem-sucedidos:</span>
                                <span className="text-green-600">{uploadState.summary?.successful_uploads || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Uploads com falha:</span>
                                <span className="text-red-600">{uploadState.summary?.failed_uploads || 0}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Taxa de sucesso:</span>
                                <span>{uploadState.summary?.success_rate || 0}%</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium">Status:</span>
                                <span>{uploadState.processingStatus}</span>
                            </div>
                        </div>

                        {uploadState.processedFiles && uploadState.processedFiles.length > 0 && (
                            <div className="mt-6 border-t pt-4">
                                <h3 className="font-semibold mb-3">Arquivos processados:</h3>
                                <div className="max-h-40 overflow-y-auto">
                                    {uploadState.processedFiles.map((file, index) => (
                                        <div key={index} className="text-sm py-1 border-b last:border-b-0">
                                            <div className="font-medium">{file.filename}</div>
                                            <div className="text-gray-500">{(file.size_bytes / 1024).toFixed(1)} KB</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                }
                footer={
                    <ActionButton
                        label="Fechar"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={handleCloseDialog}
                    />
                }
            />
        </div>
    );
};

export default OperationPageForm;
